import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { buildApiUrl } from '../config/api';
import { apiErrorMessage } from './prescriptionForm';
import {
  EMPTY_ACCOUNT_FORM,
  buildAccountRequest,
  signsOutOtherDevices,
} from './accountForm';
import CalendarSubscriptionCard from './CalendarSubscriptionCard';

/**
 * The signed-in person's own account: their username, and their password.
 *
 * # WHY THE EMAIL IS SHOWN AND NOT EDITABLE
 *
 * Changing it safely means confirming the new address before the account moves
 * to it — a typo otherwise sends every future password-reset link somewhere
 * nobody controls. That flow arrives with email verification; until then the
 * field is shown, because seeing which address holds your account is most of
 * the value, and not offered, because half of it would be a trap.
 */
const Account = () => {
  const { token, username, login } = useAuth();

  const [profile, setProfile] = useState(null);
  const [form, setForm] = useState(EMPTY_ACCOUNT_FORM);
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState(null);
  const [saving, setSaving] = useState(false);
  const [savingEmail, setSavingEmail] = useState(false);

  const authConfig = useCallback(
    () => ({ headers: { Authorization: `Bearer ${token}` } }),
    [token]
  );

  const load = useCallback(async () => {
    try {
      const response = await axios.get(buildApiUrl('/users/profile'), authConfig());
      setProfile(response.data);
      setForm({ ...EMPTY_ACCOUNT_FORM, username: response.data?.username ?? '' });
    } catch (error) {
      setStatus({ tone: 'error', message: apiErrorMessage(error, 'Could not load your account.') });
    }
  }, [authConfig]);

  useEffect(() => {
    load();
  }, [load]);

  const change = (event) => {
    const { name, value } = event.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => {
      if (!(name in prev)) return prev;
      const { [name]: _dropped, ...rest } = prev;
      return rest;
    });
  };

  // The new-block email, saved the moment it is changed. A checkbox that does
  // nothing until a "Save account" button further down is a setting people
  // believe they changed and did not. Sent alone, so it never carries a
  // half-typed username or password with it.
  const toggleNewBlockEmail = async (event) => {
    const wanted = event.target.checked;
    setStatus(null);
    setSavingEmail(true);
    setProfile((prev) => ({ ...prev, newBlockEmail: wanted }));
    try {
      await axios.put(buildApiUrl('/users/profile'), { newBlockEmail: wanted }, authConfig());
      setStatus({
        tone: 'success',
        message: wanted
          ? 'Saved. You will get an email when your coach sends a new block.'
          : 'Saved. No more new-block emails; they will still show in the app.',
      });
    } catch (error) {
      setProfile((prev) => ({ ...prev, newBlockEmail: !wanted }));
      setStatus({ tone: 'error', message: apiErrorMessage(error, 'Could not save that.') });
    } finally {
      setSavingEmail(false);
    }
  };

  const save = async (event) => {
    event.preventDefault();
    setStatus(null);

    const { errors: found, body } = buildAccountRequest(form, profile ?? {});
    if (found) {
      setErrors(found);
      return;
    }
    if (!body) {
      setStatus({ tone: 'success', message: 'Nothing to save.' });
      return;
    }

    setErrors({});
    setSaving(true);
    try {
      const response = await axios.put(buildApiUrl('/users/profile'), body, authConfig());

      // A password change evicts every token minted before it, this tab's
      // included. The server hands back a replacement; storing it is what makes
      // the difference between "your other devices were signed out" and this
      // page 401ing on its next click.
      if (response.data?.token) {
        login(response.data.token, username);
      }
      // Reloaded rather than assumed: the server trims the username, and a card
      // showing what was typed rather than what was stored is how the two drift.
      await load();
      setStatus({
        tone: 'success',
        message: signsOutOtherDevices(body)
          ? 'Saved. Your other devices have been signed out.'
          : 'Saved.',
      });
    } catch (error) {
      setStatus({ tone: 'error', message: apiErrorMessage(error, 'Could not save that.') });
    } finally {
      setSaving(false);
    }
  };

  // A wrapper, so the card stays one grid cell in Home's two columns. The
  // calendar section sits outside the form: its buttons — and Enter in its
  // link field — would otherwise submit the account.
  return (
    <div className="account-card">
      <form className="account-form" onSubmit={save}>
        <label className="field-label" htmlFor="account-username">
          Username
          <input
            id="account-username"
            className="field"
            name="username"
            value={form.username}
            onChange={change}
            aria-invalid={Boolean(errors.username)}
          />
        </label>
        {errors.username && <p className="field-error">{errors.username}</p>}

        <p className="meta-copy account-email">
          Signed in as <strong>{profile?.email ?? '…'}</strong>
          {profile && !profile.verified && ' — this address has not been confirmed.'}
        </p>

        <h3 className="kicker">Email</h3>
        {/* On unless the server says off: the server's default is on, and an
            older server that does not send the field is not one where this
            person turned anything off. */}
        <label className="field-label account-toggle" htmlFor="account-new-block-email">
          <input
            id="account-new-block-email"
            type="checkbox"
            checked={profile?.newBlockEmail !== false}
            onChange={toggleNewBlockEmail}
            disabled={!profile || savingEmail}
          />
          {' '}Email me when my coach sends a new block
        </label>

        <h3 className="kicker">Change password</h3>
        <p className="meta-copy">
          Leave these blank to keep your current password. Changing it signs out
          every other device.
        </p>

        <label className="field-label" htmlFor="account-current-password">
          Current password
          <input
            id="account-current-password"
            className="field"
            type="password"
            name="currentPassword"
            value={form.currentPassword}
            onChange={change}
            autoComplete="current-password"
            aria-invalid={Boolean(errors.currentPassword)}
          />
        </label>
        {errors.currentPassword && <p className="field-error">{errors.currentPassword}</p>}

        <label className="field-label" htmlFor="account-new-password">
          New password
          <input
            id="account-new-password"
            className="field"
            type="password"
            name="password"
            value={form.password}
            onChange={change}
            autoComplete="new-password"
            aria-invalid={Boolean(errors.password)}
          />
        </label>
        {errors.password && <p className="field-error">{errors.password}</p>}

        <label className="field-label" htmlFor="account-confirm-password">
          Repeat new password
          <input
            id="account-confirm-password"
            className="field"
            type="password"
            name="confirmPassword"
            value={form.confirmPassword}
            onChange={change}
            autoComplete="new-password"
            aria-invalid={Boolean(errors.confirmPassword)}
          />
        </label>
        {errors.confirmPassword && <p className="field-error">{errors.confirmPassword}</p>}

        {/*
          Disabled until the profile lands. Found in a browser: typing a new
          password into a form that had not finished loading validated an empty
          username and answered "Choose a username." about a field the person
          could see was filled — the form's own state was simply behind the input
          they were looking at.
        */}
        <button type="submit" className="btn-primary" disabled={saving || !profile}>
          {saving ? 'Saving…' : 'Save account'}
        </button>

        {status && (
          <p className={status.tone === 'error' ? 'status-error' : 'status-success'} role="status">
            {status.message}
          </p>
        )}
      </form>
      <CalendarSubscriptionCard />
    </div>
  );
};

export default Account;
